import type { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { toast } from 'sonner';

import { humanErrorMessage } from '@/api/errors';
import { useAuthStore } from '@/auth/store';
import { QuarantineDialog } from '@/campaigns/quarantine-dialog';
import {
  useCampaignRecipients,
  useQuarantineRecipient,
  useResolveRecipient,
  useRetryRecipient,
  type RecipientRecord,
  type RecipientStatus,
} from '@/campaigns/recipients-queries';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { DataTable } from '@/components/data-table';
import { StatusDot } from '@/components/status-dot';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useListQueryRoute } from '@/lib/use-list-query-route';

const STATUS_FILTER_VALUES: readonly RecipientStatus[] = [
  'pending',
  'enqueued',
  'sent',
  'completed',
  'quarantined',
  'resolved',
];

const ALL = '__all__';
const PAGE_SIZE = 50;

const formatDate = (iso: string): string => new Date(iso).toLocaleString('ru-RU');

interface RecipientFilter {
  status?: RecipientStatus | undefined;
}
type RecipientSort = 'includedAt' | 'status';

const isQuarantinable = (status: RecipientStatus): boolean =>
  status !== 'quarantined' && status !== 'resolved';
const isResolvable = (status: RecipientStatus): boolean => status === 'quarantined';
const isRetryable = (status: RecipientStatus): boolean => status === 'quarantined';

interface Props {
  campaignId: string;
  /** Route id for `useListQueryRoute` — defaults to the campaign detail route. */
  routeId?: string;
}

/**
 * Phase 8.5-05 — recipients-table with action menu (quarantine / resolve /
 * retry) and URL-driven list state. Backend endpoints landed in 8.5-03;
 * mutation hooks in C3. RBAC: quarantine = admin/operator, resolve =
 * admin only, retry = admin/operator. UI gates the items via role +
 * status; backend enforces independently.
 */
export const RecipientsTable = ({
  campaignId,
  routeId = '/_protected/campaigns/$campaignId',
}: Props): ReactElement => {
  const lq = useListQueryRoute<RecipientFilter, RecipientSort>({
    from: routeId,
    defaultFilter: {},
    defaultSort: { field: 'includedAt', dir: 'asc' },
    defaultPageSize: PAGE_SIZE,
  });

  const role = useAuthStore((s) => s.currentUser?.role);
  const isAdmin = role === 'admin';
  const isOperator = role === 'operator';
  const canQuarantine = isAdmin || isOperator;
  const canResolve = isAdmin;
  const canRetry = isAdmin || isOperator;

  const recipientsQuery = useCampaignRecipients(campaignId, {
    page: lq.page,
    pageSize: lq.pageSize,
    ...(lq.filter.status ? { status: lq.filter.status } : {}),
  });
  const total = recipientsQuery.data?.total ?? 0;
  const recipients = recipientsQuery.data?.items ?? [];

  const quarantine = useQuarantineRecipient(campaignId);
  const resolve = useResolveRecipient(campaignId);
  const retry = useRetryRecipient(campaignId);

  const [quarantineFor, setQuarantineFor] = useState<RecipientRecord | null>(null);
  const [quarantineError, setQuarantineError] = useState<string | undefined>(undefined);
  const [resolveFor, setResolveFor] = useState<RecipientRecord | null>(null);
  const [retryFor, setRetryFor] = useState<RecipientRecord | null>(null);

  const handleQuarantine = (reason: string): void => {
    if (!quarantineFor) return;
    setQuarantineError(undefined);
    quarantine.mutate(
      { id: quarantineFor.id, reason },
      {
        onSuccess: () => {
          toast.success('Получатель отправлен в карантин');
          setQuarantineFor(null);
        },
        onError: (err) => {
          setQuarantineError(humanErrorMessage(err));
        },
      },
    );
  };

  const handleResolve = (): void => {
    if (!resolveFor) return;
    resolve.mutate(resolveFor.id, {
      onSuccess: () => {
        toast.success('Получатель помечен как resolved');
        setResolveFor(null);
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  const handleRetry = (): void => {
    if (!retryFor) return;
    retry.mutate(retryFor.id, {
      onSuccess: () => {
        toast.success('Получатель возвращён в pending');
        setRetryFor(null);
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  const columns: ColumnDef<RecipientRecord>[] = [
    {
      id: 'tradeLinkId',
      header: 'Trade-link ID',
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.tradeLinkId}</span>,
    },
    {
      id: 'tradeLinkUrl',
      header: 'Trade URL',
      cell: ({ row }) => {
        const url = row.original.tradeLinkUrl;
        if (!url) return <span className="text-muted-foreground">—</span>;
        return (
          <span className="block max-w-[40ch] truncate font-mono text-xs" title={url}>
            {url}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Статус',
      meta: { sortField: 'status' },
      cell: ({ row }) => <StatusDot domain="recipient" state={row.original.status} />,
    },
    {
      id: 'includedAt',
      header: 'Добавлен',
      meta: { sortField: 'includedAt' },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{formatDate(row.original.includedAt)}</span>
      ),
    },
    {
      id: 'lastError',
      header: 'Последняя ошибка',
      cell: ({ row }) => {
        const err = row.original.lastError;
        if (!err) return <span className="text-muted-foreground">—</span>;
        return (
          <span className="block max-w-[32ch] truncate text-xs text-destructive" title={err}>
            {err}
          </span>
        );
      },
    },
    {
      id: 'quarantinedReason',
      header: 'Причина quarantine',
      cell: ({ row }) => row.original.quarantinedReason ?? '—',
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const r = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Действия">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={!canQuarantine || !isQuarantinable(r.status)}
                onSelect={(e) => {
                  e.preventDefault();
                  setQuarantineError(undefined);
                  setQuarantineFor(r);
                }}
              >
                Карантин…
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!canResolve || !isResolvable(r.status)}
                onSelect={(e) => {
                  e.preventDefault();
                  setResolveFor(r);
                }}
              >
                Resolve
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!canRetry || !isRetryable(r.status)}
                onSelect={(e) => {
                  e.preventDefault();
                  setRetryFor(r);
                }}
              >
                Retry
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const filterSlot = (
    <>
      <Select
        value={lq.filter.status ?? ALL}
        onValueChange={(v) => {
          lq.updateQuery({ filter: { status: v === ALL ? undefined : (v as RecipientStatus) } });
        }}
      >
        <SelectTrigger className="w-48">
          <SelectValue placeholder="Все статусы" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Все статусы</SelectItem>
          {STATUS_FILTER_VALUES.map((s) => (
            <SelectItem key={s} value={s}>
              {s}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-sm text-muted-foreground">Всего: {total}</span>
    </>
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={recipients}
        emptyMessage="Recipients не найдены"
        filterSlot={filterSlot}
        sort={{ field: lq.sort.field, dir: lq.sort.dir }}
        onSortChange={(next) => {
          lq.updateQuery({ sort: { field: next.field as RecipientSort | null, dir: next.dir } });
        }}
        pagination={{
          page: lq.page,
          pageSize: lq.pageSize,
          total,
          onPageChange: (page) => {
            lq.updateQuery({ page });
          },
        }}
      />

      <QuarantineDialog
        open={quarantineFor !== null}
        onOpenChange={(next) => {
          if (!next) {
            setQuarantineFor(null);
            setQuarantineError(undefined);
          }
        }}
        isPending={quarantine.isPending}
        {...(quarantineError !== undefined ? { submitError: quarantineError } : {})}
        onSubmit={handleQuarantine}
      />

      <ConfirmDialog
        open={resolveFor !== null}
        onOpenChange={(next) => {
          if (!next) setResolveFor(null);
        }}
        title="Подтвердить получателя?"
        description="Получатель будет помечен resolved (terminal). Действие не отменяется через retry."
        onConfirm={handleResolve}
        isPending={resolve.isPending}
        confirmLabel="Подтвердить"
        pendingLabel="Подтверждаем…"
      />

      <ConfirmDialog
        open={retryFor !== null}
        onOpenChange={(next) => {
          if (!next) setRetryFor(null);
        }}
        title="Повторить отправку?"
        description="Получатель будет возвращён в pending — driver попробует доставить ещё раз."
        onConfirm={handleRetry}
        isPending={retry.isPending}
        confirmLabel="Повторить"
        pendingLabel="Запускаем…"
      />
    </>
  );
};
